import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import { INV_API_VENDORS } from '../../../../../reusable-components/datagrid/vendorColumns';
import Iconify from '../../../../../reusable-components/iconify';

VendorSelectStep.propTypes = {
  selectedBookingTypeLabel: PropTypes.string,
  counts: PropTypes.shape({
    newCount: PropTypes.number,
    updatedCount: PropTypes.number,
  }),
  isRefreshLoading: PropTypes.bool,
  progress: PropTypes.number,
  filteredData: PropTypes.array,
  rowSelectionModel: PropTypes.array,
  setRowSelectionModel: PropTypes.func,
  setCounts: PropTypes.func,
  isLoading: PropTypes.bool,
};

export function VendorSelectStep({
  selectedBookingTypeLabel,
  counts,
  setCounts,
  isRefreshLoading,
  progress,
  filteredData,
  rowSelectionModel,
  setRowSelectionModel,
  isLoading,
}) {

  useEffect(() => {
    setCounts({ newCount: 0, updatedCount: 0 });
    setRowSelectionModel([]);
  }, []);

  const customToolbar = useCallback(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          height: '30px',
          bgcolor: 'background.neutral',
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={500}>
            {selectedBookingTypeLabel}
          </Typography>
          <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
          {isRefreshLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress variant="determinate" color="warning" size={14} value={progress} />
              <Typography variant="caption" color="warning.main">
                Refreshing {progress}%
              </Typography>
            </Box>
          ) : (
            <>
              {' '}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: 'info.lighter',
                  color: 'info.dark',
                  borderRadius: 999,
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <Iconify icon="eva:plus-outline" width={12} />
                {`${counts.newCount} new ${counts.newCount === 1 ? 'vendor' : 'vendors'}`}{' '}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  border: '0.5px solid',
                  borderColor: 'divider',
                  borderRadius: 999,
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                <Iconify icon="eva:refresh-outline" width={12} />
                {`${counts.updatedCount} updated ${counts.updatedCount === 1 ? 'vendor' : 'vendors'}`}
              </Box>
            </>
          )}
        </Box>
      </Box>
    ),
    [selectedBookingTypeLabel, counts, isRefreshLoading, progress]
  );

  return (
    <CustomDataGrid
      checkboxSelection
      onRowSelectionModelChange={(newModel) => {
        if (!isRefreshLoading) setRowSelectionModel(newModel);
      }}
      keepNonExistentRowsSelected
      rowSelectionModel={rowSelectionModel}
      gridId="inv-api-vendors"
      CustomLeftToolbar={customToolbar}
      tableBoxSX={{ height: '330px' }}
      boxSX={{ height: '400px' }}
      scrollbarHeight="250px"
      data={filteredData || []}
      gridColumns={INV_API_VENDORS}
      pinnedColumns={{ right: ['actions'] }}
      sort={[{ field: 'name', sort: 'asc' }]}
      isLoading={isLoading}
    />
  );
}
